import mixpanel from 'mixpanel-browser';
import { Logger } from '../support/Logger';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { EnvConfiguration } from '../config/EnvConfiguration';
import { UserState } from '../user/UserState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { DomainToMixPanelAdapter } from './DomainToMixPanelAdapter';
import { MixPanelPageViewEventEmitter } from './MixPanelPageViewEventEmitter';
import { SessionReplayService, SESSION_REPLAY_INIT_COMPLETE, EXCLUDE_ADMINS_FROM_REPLAY } from './SessionReplayService';
import { Container } from '@meraki-internal/react-dependency-injection';
import { UAParser } from 'ua-parser-js';
import { DeviceInfo } from '../support/DeviceInfo';
import { DisplayMetricsProvider } from '../support/DisplayMetricsProvider';
import { AppInfo } from '../support/AppInfo';

export type ITrackingContext = {[key: string]: any};

export type ITrackingDevice = {
    // App info
    appVersion: string;
    appSha: string;

    // Device properties
    platform: DeviceInfo['platform'];
    osVersion: string;
    manufacturer: string;
    model: string;
    uuid: string;

    // Browser info (web only)
    browserName?: string;
    browserVersion?: string;

    // Display metrics
    screenWidth: number;
    screenHeight: number;
    viewportWidth: number;
    viewportHeight: number;
    devicePixelRatio: number;

    // omitted when DeviceStatusBar hasn't initialized yet
    statusBarHeight?: number;
    navigationBarHeight?: number;
};

/**
 * This is a facade around mixpanel service and it supporting classes so consumers don't
 * have to reason about the inner workings. It also helps to use code to document how
 * it is all glued together
 *
 * It primarily trying to encapsulate
 *
 * 1. Lots to init on app start, and in the right order
 */
export class MixPanelServiceFacade {
    static inject = () => [
        Container
    ];
    constructor(
        private container: Container
    ){}

    init = async () => {
        const log = this.container.get(Logger);
        try {
            const mixpanelService = this.container.get(MixpanelService);
            const domainToMixPanelAdapter = this.container.get(DomainToMixPanelAdapter);
            const mixPanelPageViewEventEmitter = this.container.get(MixPanelPageViewEventEmitter);
            const principal = this.container.get(AppPrincipal);
            const user = this.container.get(UserState);
            await mixpanelService.init({ principal, user });

            domainToMixPanelAdapter.init();

            mixPanelPageViewEventEmitter.init();

            this.container.get(SessionReplayService).init({ principal });
        }
        catch (err: any) {
            log.info(`MixPanelServiceFacade.init failed. Attempting to a minimial mixpanel init. Likely failed b/c the container failed to fully start, eg invalid device. Erorr: ${err.toString()}`);
            await this.container.get(MixpanelService).init({ });
        }

    };
}


/**
 * This is the class that actually talks to mix panel.
 *
 * Other classes talk to it through BufferedEventBridge
 *
 * This is not exported on purpose, see MixPanelServiceFacade and MixPanelEventEmitter
 */
class MixpanelService {
    static inject = () => [
        // only dependencies that are guaranteed to inject
        // all others should go through init and MixPanelServiceFacade
        BufferedEventBus,
        Logger,
        UAParser,
        EnvConfiguration,
        DeviceInfo,
        DisplayMetricsProvider,
        AppInfo,
        SessionReplayService
    ];
    constructor(
        private events: BufferedEventBus,
        private log: Logger,
        private uaParser: UAParser,
        private config: EnvConfiguration,
        private deviceInfo: DeviceInfo,
        private displayMetrics: DisplayMetricsProvider,
        private appInfo: AppInfo,
        private sessionReplay: SessionReplayService
    ){}

    private status: 'instantiated' | 'initializing' | 'initialized' = 'instantiated';

    private contextForAll: ITrackingContext = {};

    init = async ({ principal, user }: { principal?: AppPrincipal; user?: UserState; }) => {
        this.status = 'instantiated';
        if (this.status !== 'instantiated'){
            // don't init again, this should only happen when app start failed
            // and we aren't sure if init was called or not
            return;
        }

        this.log.info('TrackingService.init', { principal, user: user?.state });

        try {
            const device = this.uaParser.getDevice();

            this.contextForAll = {
                'Operating System Version': this.uaParser.getOS().version,
                'Device Vendor': device.vendor || '[No Vendor]',
                'Device Model': device.model || '[No Model]',
                'Device Type': device.type || '[No Type]'
            };

            if (!this.config.MIXPANEL_TOKEN) {
                this.log.info(`Disabling tracker for "${this.config.ENV}" env b/c no token`);
                return;
            }

            this.log.info(`Initializing tracker for "${this.config.ENV}" env`);
            mixpanel.init(this.config.MIXPANEL_TOKEN, {
                debug: false,
                persistence: 'localStorage',

                // The SDK hardcodes maskAllInputs:true in rrweb, so <input>/<textarea>/<select> are
                // always masked regardless of this config. record_mask_text_selector defaults to '*'
                // which masks every text node; override with a class selector so only opt-in
                // ".mp-mask" elements mask. Trade-off: text rendered from user input (e.g. chat
                // bubbles, names) will be visible in replays — we accept that for now and will
                // add field-level masking later if needed.
                record_mask_text_selector: '.mp-mask',

                // record_block_selector defaults to 'img, video, audio'; blocked
                // elements have their whole subtree removed during serialization,
                // so by default NO image ever appears in a replay. Drop 'img' to
                // restore images (the only one of the three with replay value:
                // audio is unhearable in a replay and rrweb never captures video
                // frames). This intentionally records user-supplied images
                // (e.g. note attachments) into replays. There is no per-element
                // unblock, so this is all-or-nothing for images.
                record_block_selector: 'video, audio'

                // record_heatmap_data is intentionally NOT enabled. When on, the SDK
                // emits an [Auto] Element Click ($mp_click) event for every click to
                // power click heatmaps, which floods the event stream. Session replay
                // recording (start_session_recording) does not depend on it.
            });

            this.log.info('init -- mixpanel.get_distinct_id()', mixpanel.get_distinct_id());
            this.status = 'initialized';

            // Verify any IDB-resumed session replay belongs to the current principal
            // BEFORE mixpanel.identify runs; identify retroactively re-tags an active
            // replay with the new distinct_id, so a leftover from a previous user
            // would be re-attributed to whoever signs in next. This must run after
            // mixpanel.init (so the recorder state is observable) and before identify.
            await this.sessionReplay.enforceReplayOwnership(principal);

            // IMPORTANT: do not send userId to mixpanel if anonymous
            // (allow mixpanel to assign a device id and auto-merge at sign-in)
            if (principal && !isAnonymousViewer(principal)) {
                const performIdentify = () => {
                    mixpanel.identify(principal.userId);
                    mixpanel.people.set({
                        tokenType: principal.tokenType,
                        '$email': principal.email
                    });
                };

                if (EXCLUDE_ADMINS_FROM_REPLAY && principal.isAdmin) {
                    // Defer identify until SessionReplayService has stopped any anonymous replay
                    // carried forward from the logged-out state. Otherwise the admin's distinct_id
                    // and email briefly tag the replay before stop completes, and the admin appears
                    // in the Mixpanel replay list.
                    this.events.on(SESSION_REPLAY_INIT_COMPLETE, performIdentify);
                } else {
                    performIdentify();
                }
            }

            this.events.on('MixPanelService.track', (payload) => {
                const { event, context } = payload as { event: string, context: ITrackingContext; };
                this.sendToMixPanel(event, context);
            });

            this.events.on('MixPanelService.care-recipient-relationships', payload => {
                this.setRecipientRelationships(payload as string[]);
            });

            this.events.on('AuthService.signing-out', () => {
                this.reset();
            });

            this.events.on('MixPanelService.set-profile-prop', payload => {
                this.setProfileProp(payload as {prop: string, val: any});
            });

            this.events.on('MixPanelService.increment-profile-prop', payload => {
                this.incrementProfileProp(payload as {prop: string, by: number});
            });

            // don't add more bindings here unless they need access to internal like signing out needs to clear the mixpanel cookies / local storage
            // if you just want to bind to send a mixpanel event, consider
            // 1. MixPanelEventEmitter if you just need to send an event
            // 2. DomainToMixPanelAdapter if you want to bind a domain event to a mixpanel event (eg UserState.saved)

        } catch (e: any) {
            this.log.error(new Error(`Failed to initialize tracking: ${e.message}`));
        }
    };

    setRecipientRelationships = (relationships: string[]) => {
        if (!this.log){
            throw new Error('expected init before this method was called');
        }
        this.log.info('TrackingService.setRelationshipToCaremap', relationships);
        try{
            if (this.status === 'initialized'){
                mixpanel.people.set('relationships', relationships);
            }
        } catch (err: any){
            this.log.error(new Error(`Failed to setRelationshipToCaremap: ${err.message}`));
        }
    };

    private reset = () => {
        try {
            if (this.status === 'initialized') {
                mixpanel.reset();
            }

        } catch (e: any) {
            this.log.error(new Error(`Failed to reset tracker: ${e.message}`));
        }
    };

    private getDeviceInfo = (): ITrackingDevice => {
        const metrics = this.displayMetrics.getMetrics();

        const deviceInfo: ITrackingDevice = {
            appVersion: this.appInfo?.version || '',
            appSha: process.env.VITE_APP_SHA || '',
            platform: this.deviceInfo.platform,
            osVersion: this.deviceInfo.osVersion || this.deviceInfo.operatingSystem || '',
            manufacturer: this.deviceInfo.manufacturer || '',
            model: this.deviceInfo.model || '',
            uuid: this.deviceInfo.uuid || '',
            browserName: this.deviceInfo.browserName,
            browserVersion: this.deviceInfo.browserVersion,
            screenWidth: metrics.screen.width,
            screenHeight: metrics.screen.height,
            viewportWidth: metrics.viewport.width,
            viewportHeight: metrics.viewport.height,
            devicePixelRatio: metrics.devicePixelRatio,

            // omit (don't send undefined) when DeviceStatusBar hasn't initialized yet, e.g.
            // when tracking fires during a failed app start before DeviceStatusBar.init ran
            ...(metrics.statusBarHeight !== undefined && { statusBarHeight: metrics.statusBarHeight }),
            ...(metrics.navigationBarHeight !== undefined && { navigationBarHeight: metrics.navigationBarHeight })
        };

        this.log.info('Device info for tracking:', deviceInfo);

        return deviceInfo;
    };

    private sendToMixPanel = (event: string, context?: ITrackingContext) => {
        context = {...context, ...this.contextForAll, ...this.getDeviceInfo()};
        this.log.info(`Track "${event}"`, context);

        if (this.status === 'initialized') {
            mixpanel.track(event, context);
        }
    };

    private setProfileProp = ({prop, val}: {prop: string, val: any}) => {
        // console.log(prop, val);
        if (this.status === 'initialized'){
            mixpanel.people.set(prop, val);
        }
    };

    private incrementProfileProp = ({prop, by}: {prop: string, by: number}) => {
        // console.log(prop, by);
        if (this.status === 'initialized'){
            mixpanel.people.increment(prop, by);
        }
    };
}
